/**
 * SWR-based data fetching hooks for global data synchronization
 * This provides automatic caching, revalidation, and real-time sync across components
 */
import useSWR, { mutate } from 'swr';
import { useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get token from localStorage
const getToken = () => localStorage.getItem('session_token');

// Base fetcher that handles auth and error states
const fetcher = async (url) => {
  const token = getToken();
  if (!token) throw new Error('No session token');
  
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!res.ok) {
    const error = new Error('Failed to fetch data');
    error.status = res.status;
    throw error;
  }
  
  return res.json();
};

// SWR config with automatic revalidation - disabled focus revalidation to reduce flicker
const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 5000,
  errorRetryCount: 2,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    // Never retry on 403, 429, or 401
    if (error.status === 403 || error.status === 429 || error.status === 401) return;
    if (retryCount >= 2) return;
    setTimeout(() => revalidate({ retryCount }), 5000 * (retryCount + 1));
  },
};

/**
 * GLOBAL SYNC EVENT - Dispatch this to trigger sync across all pages
 * Usage: window.dispatchEvent(new CustomEvent('phygital:sync', { detail: { type: 'user' | 'event' | 'all', eventId?: string } }))
 */
export function useSyncListener() {
  useEffect(() => {
    const handleSync = async (e) => {
      const { type, eventId } = e.detail || {};
      console.log('[Sync] Received sync event:', type, eventId);
      
      if (type === 'event' && eventId) {
        await revalidateEventData(eventId);
      } else if (type === 'user' || type === 'all') {
        await revalidateAllUserData();
      }
      
      if (type === 'all' && eventId) {
        await revalidateEventData(eventId);
      }
    };
    
    window.addEventListener('phygital:sync', handleSync);
    return () => window.removeEventListener('phygital:sync', handleSync);
  }, []);
}

/**
 * Trigger global sync across all pages/components
 */
export function triggerGlobalSync(type = 'all', eventId = null) {
  console.log('[Sync] Triggering global sync:', type, eventId);
  window.dispatchEvent(new CustomEvent('phygital:sync', { detail: { type, eventId } }));
  
  // Also dispatch user-updated event for AuthContext
  if (type === 'user' || type === 'all') {
    window.dispatchEvent(new CustomEvent('phygital:user-updated', { detail: {} }));
  }
}

/**
 * Hook for current user data
 */
export function useUser() {
  const { data, error, isLoading, mutate: mutateUser } = useSWR(
    getToken() ? `${API}/me` : null,
    fetcher,
    swrConfig
  );
  
  return {
    user: data,
    isLoading,
    isError: error,
    mutateUser,
  };
}

/**
 * Hook for user preferences
 */
export function usePreferences() {
  const { data, error, isLoading, mutate: mutatePreferences } = useSWR(
    getToken() ? `${API}/user/preferences` : null,
    fetcher,
    swrConfig
  );
  
  return {
    preferences: data || {},
    isLoading,
    isError: error,
    mutatePreferences,
  };
}

/**
 * Hook for user defaults
 */
export function useDefaults() {
  const { data, error, isLoading, mutate: mutateDefaults } = useSWR(
    getToken() ? `${API}/user/defaults` : null,
    fetcher,
    swrConfig
  );
  
  return {
    defaults: data,
    isLoading,
    isError: error,
    mutateDefaults,
  };
}

/**
 * Hook for user experiences
 */
export function useExperiences() {
  const { data, error, isLoading, mutate: mutateExperiences } = useSWR(
    getToken() ? `${API}/user/experiences` : null,
    fetcher,
    swrConfig
  );
  
  return {
    experiences: data?.experiences || data || [],
    isLoading,
    isError: error,
    mutateExperiences,
  };
}

/**
 * Hook for user inspirations
 */
export function useInspirations() {
  const { data, error, isLoading, mutate: mutateInspirations } = useSWR(
    getToken() ? `${API}/user/inspirations` : null,
    fetcher,
    swrConfig
  );
  
  return {
    inspirations: data || [],
    isLoading,
    isError: error,
    mutateInspirations,
  };
}

/**
 * Hook for hosted events
 */
export function useHostedEvents() {
  const { data, error, isLoading, mutate: mutateHostedEvents } = useSWR(
    getToken() ? `${API}/user/hosted-events` : null,
    fetcher,
    swrConfig
  );
  
  return {
    hostedEvents: data?.events || [],
    isLoading,
    isError: error,
    mutateHostedEvents,
  };
}

/**
 * Hook for attending events
 */
export function useAttendingEvents() {
  const { data, error, isLoading, mutate: mutateAttendingEvents } = useSWR(
    getToken() ? `${API}/user/attending-events` : null,
    fetcher,
    swrConfig
  );
  
  return {
    attendingEvents: data?.events || [],
    isLoading,
    isError: error,
    mutateAttendingEvents,
  };
}

/**
 * Hook for organizations
 */
export function useOrganizations() {
  const { data, error, isLoading, mutate: mutateOrganizations } = useSWR(
    getToken() ? `${API}/organizations` : null,
    fetcher,
    swrConfig
  );
  
  return {
    organizations: data || [],
    isLoading,
    isError: error,
    mutateOrganizations,
  };
}

/**
 * Hook for event RSVP data (for host dashboard)
 */
export function useEventRsvps(eventId) {
  const { data, error, isLoading, mutate: mutateRsvps } = useSWR(
    eventId && getToken() ? `${API}/events/${eventId}/rsvps` : null,
    fetcher,
    swrConfig
  );
  
  return {
    rsvps: data?.rsvps || [],
    totalCount: data?.total || 0,
    isLoading,
    isError: error,
    mutateRsvps,
  };
}

/**
 * Hook for single event data
 */
export function useEvent(eventId) {
  const { data, error, isLoading, mutate: mutateEvent } = useSWR(
    eventId && getToken() ? `${API}/events/${eventId}` : null,
    fetcher,
    swrConfig
  );
  
  return {
    event: data,
    isLoading,
    isError: error,
    mutateEvent,
  };
}

/**
 * Hook for notifications
 */
export function useNotifications() {
  const { data, error, isLoading, mutate: mutateNotifications } = useSWR(
    getToken() ? `${API}/notifications` : null,
    fetcher,
    { ...swrConfig, refreshInterval: 30000, revalidateOnFocus: false, revalidateOnMount: true } // Poll silently, no UI flicker
  );
  
  return {
    notifications: data?.notifications || [],
    unreadCount: data?.unread_count || 0,
    isLoading: isLoading && !data, // Only show loading on first fetch, not revalidation
    isError: error,
    mutateNotifications,
  };
}

/**
 * Global mutation helper to revalidate all user data
 * Call this after any update operation
 */
export async function revalidateAllUserData() {
  const token = getToken();
  if (!token) return;
  
  // Revalidate all user-related data
  await Promise.all([
    mutate(`${API}/me`),
    mutate(`${API}/user/preferences`),
    mutate(`${API}/user/defaults`),
    mutate(`${API}/experiences`),
    mutate(`${API}/user/inspirations`),
    mutate(`${API}/user/hosted-events`),
    mutate(`${API}/user/attending-events`),
    mutate(`${API}/organizations`),
    mutate(`${API}/notifications`),
  ]);
}

/**
 * Revalidate event-specific data
 */
export async function revalidateEventData(eventId) {
  if (!eventId) return;
  
  await Promise.all([
    mutate(`${API}/events/${eventId}`),
    mutate(`${API}/events/${eventId}/rsvps`),
  ]);
}

/**
 * Combined hook for all user data (for backwards compatibility)
 */
export function useAllUserData() {
  const { user, isLoading: userLoading, mutateUser } = useUser();
  const { preferences, isLoading: prefsLoading, mutatePreferences } = usePreferences();
  const { defaults, isLoading: defaultsLoading, mutateDefaults } = useDefaults();
  const { experiences, isLoading: expLoading, mutateExperiences } = useExperiences();
  const { inspirations, isLoading: inspoLoading, mutateInspirations } = useInspirations();
  const { hostedEvents, isLoading: hostedLoading, mutateHostedEvents } = useHostedEvents();
  const { attendingEvents, isLoading: attendingLoading, mutateAttendingEvents } = useAttendingEvents();
  const { organizations, isLoading: orgsLoading, mutateOrganizations } = useOrganizations();
  const { notifications, unreadCount, isLoading: notifLoading, mutateNotifications } = useNotifications();
  
  const isLoading = userLoading || prefsLoading || defaultsLoading || expLoading || 
                    inspoLoading || hostedLoading || attendingLoading || orgsLoading;
  // Note: notifLoading excluded intentionally - notifications poll every 30s
  // and including it causes visible re-renders/flicker on /me page
  
  return {
    user,
    preferences,
    defaults,
    experiences,
    inspirations,
    hostedEvents,
    attendingEvents,
    organizations,
    notifications,
    unreadCount,
    isLoading,
    // Mutation functions
    mutateUser,
    mutatePreferences,
    mutateDefaults,
    mutateExperiences,
    mutateInspirations,
    mutateHostedEvents,
    mutateAttendingEvents,
    mutateOrganizations,
    mutateNotifications,
    // Bulk revalidation
    revalidateAll: revalidateAllUserData,
  };
}

export { mutate };
